import React, {forwardRef} from 'react'
import {Box, BoxProps, useMultiStyleConfig} from '@chakra-ui/react'
import {useIntl} from 'react-intl'
import {Icon} from '_uiComponents'

interface QuickAddButtonProps {
    onClick: React.MouseEventHandler<HTMLButtonElement>
    shouldOpenOverlay?: boolean
    listingLayout?: 'grid3' | 'grid2' | 'wishlist' | 'shoptheimage' | 'carousel'
    productName: string
    productId: string
}

/**
 * @name QuickAddButton
 *
 * @component
 *
 * @description
 * Renders a button designed for quick addition of products.
 *
 * @remarks
 * This button can toggle between two states, represented by different icons,
 * and can optionally open an overlay for viewing product details such as sizes and colors.
 *
 * @param {QuickAddButtonProps} props - The properties passed to the component.
 * @param {React.MouseEventHandler<HTMLButtonElement>} props.onClick - Callback function triggered when the button is clicked.
 * @param {boolean} [props.shouldOpenOverlay] - Optional flag indicating whether the overlay should be opened.
 * @param {'grid3' | 'grid2' | 'wishlist'} [props.listingLayout] - Optional layout style for listing the product.
 * @param {string} props.productName - The name of the product to be displayed in the overlay.
 * @param {string} props.productId - The ID of the related product.
 *
 * @returns {JSX.Element} A JSX element representing the `QuickAddButton`.
 *
 * @example
 * ```tsx
 * <QuickAddButton
 *  onClick={handleClick}
 *  shouldOpenOverlay={true}
 *  listingLayout="grid3"
 *  productName="T-shirt"
 * />
 * ```
 */
const QuickAddButton = forwardRef<HTMLButtonElement, QuickAddButtonProps>(({onClick, shouldOpenOverlay, listingLayout, productName, productId}, ref) => {
    const styles = useMultiStyleConfig('QuickAddButton', {listingLayout}) as Record<string, BoxProps>
    const {formatMessage} = useIntl()

    const ariaLabelShow = formatMessage(
        {
            id: 'wishlist.product.overlay.open.aria',
            defaultMessage: 'View available {0} Sizes and Colors',
        },
        {0: productName},
    )

    const ariaLabelHide = formatMessage(
        {
            id: 'wishlist.product.overlay.hide.aria',
            defaultMessage: 'Hide available {0} Sizes and Colors',
        },
        {0: productName},
    )

    const ariaLabel = !shouldOpenOverlay ? ariaLabelShow : ariaLabelHide

    return (
        <Box
            ref={ref}
            as="button"
            {...styles.wrapper}
            aria-label={ariaLabel}
            onClick={onClick}
            data-testid={`quick-add-button`}
            aria-controls={`product-tile-overlay-${productId}`}
            aria-expanded={shouldOpenOverlay}
        >
            <Icon name={!shouldOpenOverlay ? 'IconPlus' : 'IconMinus'} size="md" />
        </Box>
    )
})

QuickAddButton.displayName = 'QuickAddButton'

export {QuickAddButton}
